import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ClockIcon, MapPinIcon, MenuIcon, PhoneIcon, XIcon } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { business } from "../data/business";
import { navLinks } from "../data/navigation";
import { ui } from "../data/ui";
import { useScrollPast } from "../hooks/useScrollPast";
import { LanguageToggle } from "./LanguageToggle";
import { Logo } from "./Logo";
export function Header() {
  const {
    t
  } = useLanguage();
  const scrolled = useScrollPast(24);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return <header className="sticky top-0 z-50" data-id="element-188" data-mp="2wkkz5e59tq4a">
      <div className="hidden border-b border-white/10 bg-charcoal md:block" data-id="element-189" data-mp="2wmfiwp4t3c6s">
        <div className="mx-auto flex h-9 max-w-7xl items-center justify-between px-5 text-xs text-white/70 sm:px-8" data-id="element-190" data-mp="2rkq5x3y0wog2">
          <div className="flex items-center gap-6" data-id="element-191" data-mp="2mngbf9vs1mld">
            <a href={business.mapsUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-white" data-id="element-192" data-mp="2hnttin507i68">
              <MapPinIcon className="h-3.5 w-3.5 text-gold" aria-hidden="true" data-id="element-193" data-mp="3faaovmu6flpc" />
              {business.street}, {business.city}, {business.region} {business.postalCode}
            </a>
            <span className="hidden items-center gap-1.5 lg:flex" data-id="element-194" data-mp="35kr6eiadltv0">
              <ClockIcon className="h-3.5 w-3.5 text-gold" aria-hidden="true" data-id="element-195" data-mp="35j6ez1jcyr1d" />
              {t(ui.common.hours)}
            </span>
          </div>
          <div className="flex items-center gap-5" data-id="element-196" data-mp="1xhc1ossslud">
            <a href={business.phoneHref} className="flex items-center gap-1.5 font-semibold text-gold hover:text-gold-bright" data-id="element-197" data-mp="3tb3migfrywwo">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-198" data-mp="1rdba8jitesp2" />
              {business.phoneDisplay}
            </a>
            <LanguageToggle data-id="element-199" data-mp="1wbcuv8vw302n" />
          </div>
        </div>
      </div>

      <div className={`border-b bg-ink/95 backdrop-blur transition-[border-color] duration-200 ease-out ${scrolled ? "border-gold/30" : "border-transparent"}`} data-id="element-200" data-mp="1ho9wz13nmpes">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8" data-id="element-201" data-mp="18mk3zqu4jiew">
          <a href="#home" aria-label="Flexhire Solutions LLC — Home" className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-202" data-mp="1cp6spivi0n7v">
            <Logo data-id="element-203" data-mp="yr8qfbugch8q" />
          </a>

          <nav aria-label="Primary" className="hidden lg:block" data-id="element-204" data-mp="13n77eedgawz4">
            <ul className="flex items-center gap-7" data-id="element-205" data-mp="13ptop5q58quj">
              {navLinks.map(link => <li key={link.href} data-id="element-206" data-mp="2t140o49ms9nr">
                  <a href={link.href} className="link-slide whitespace-nowrap py-1 text-sm font-medium text-white/85 hover:text-gold" data-id="element-207" data-mp="2y29t00zdpuwr">
                    {t(link.label)}
                  </a>
                </li>)}
            </ul>
          </nav>

          <div className="flex items-center gap-3" data-id="element-208" data-mp="hdqq2zckcxnm">
            <a href="#careers" className="btn-outline hidden px-5 xl:inline-flex" data-id="element-209" data-mp="w5j1j098lrk2">
              {t(ui.common.joinTeam)}
            </a>
            <a href="#contact" className="btn-gold hidden px-5 sm:inline-flex" data-id="element-210" data-mp="rbod5jiosoen">
              {t(ui.common.getQuote)}
            </a>
            <a href={business.phoneHref} aria-label={`${t(ui.common.callNow)} ${business.phoneDisplay}`} className="grid h-11 w-11 place-items-center rounded-sm border border-gold/60 text-gold md:hidden" data-id="element-211" data-mp="106ktcw0tr1c0">
              <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-212" data-mp="1a260f8kr2rsm" />
            </a>
            <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? t(ui.common.closeMenu) : t(ui.common.openMenu)} className="grid h-11 w-11 place-items-center rounded-sm text-white hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold lg:hidden" data-id="element-213" data-mp="3gv8gdd9xxdcr">
              {open ? <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-214" data-mp="2gyydstik2i1k" /> : <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-215" data-mp="2gxna0tp41aag" />}
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-216" data-mp="1k6coc8tmjro3">
          {open && <motion.div id="mobile-menu" initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-h-[calc(100svh-80px)] overflow-y-auto border-t border-gold/30 bg-ink lg:hidden" data-id="element-217" data-mp="1p8bufqgr12oq">
              <nav aria-label="Mobile" className="mx-auto max-w-7xl px-5 py-6 sm:px-8" data-id="element-218" data-mp="15irqatblwin1">
                <ul className="divide-y divide-white/10" data-id="element-219" data-mp="xzigiyz6uhxg">
                  {navLinks.map(link => <li key={link.href} data-id="element-220" data-mp="t05tad4en8uc">
                      <a href={link.href} onClick={() => setOpen(false)} className="flex min-h-[52px] items-center font-display text-xl font-semibold uppercase text-white hover:text-gold" data-id="element-221" data-mp="t1736e9oppah">
                        {t(link.label)}
                      </a>
                    </li>)}
                </ul>
                <div className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-222" data-mp="a8360ix9cub4">
                  <a href="#contact" onClick={() => setOpen(false)} className="btn-gold w-full" data-id="element-223" data-mp="5c4l55b51ufg">
                    {t(ui.common.getEstimate)}
                  </a>
                  <a href="#careers" onClick={() => setOpen(false)} className="btn-outline w-full" data-id="element-224" data-mp="3sfj6pjlwnf72">
                    {t(ui.common.joinTeam)}
                  </a>
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5" data-id="element-225" data-mp="3dt3qkmi70axe">
                  <a href={business.phoneHref} className="flex items-center gap-2 font-semibold text-gold" data-id="element-226" data-mp="3is6s3wyyrz0m">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-227" data-mp="33v64o5nt2q7y" />
                    {business.phoneDisplay}
                  </a>
                  <LanguageToggle data-id="element-228" data-mp="38usgcanzjyen" />
                </div>
              </nav>
            </motion.div>}
        </AnimatePresence>
      </div>
    </header>;
}